import { BaseFeature } from '../../core/base-feature.js';

/**
 * WebcamSamplerFeature - Frontend feature for controlling the desktop's webcam
 * Handles: activation, deactivation, sampling, status updates
 */
export class WebcamSamplerFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.webcamActive = false;
        this.samplingEnabled = false;
        this.samplingInterval = 5 * 60 * 1000;
        this.sampleCount = 0;
        this.lastSampleTimestamp = null;
        this.recentSamples = [];

        // Event callbacks for React components
        this.statusCallbacks = new Set();
        this.sampleCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'webcam-sampler',
            name: 'Webcam Sampler',
            version: 'v1.0',
            capabilities: [
                'activate',
                'deactivate',
                'sample',
                'start_sampling',
                'stop_sampling',
                'get_status',
                'configure'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[WebcamSampler] Initializing...');
        console.log('[WebcamSampler] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Responses
            case 'activate_response':
                return this._handleActivateResponse(payload);
            case 'deactivate_response':
                return this._handleDeactivateResponse(payload);
            case 'sample_response':
                return this._handleSampleResponse(payload);
            case 'start_sampling_response':
                return this.handleResponse('start_sampling', payload);
            case 'stop_sampling_response':
                return this.handleResponse('stop_sampling', payload);
            case 'get_status_response':
                return this._handleStatusResponse(payload);
            case 'configure_response':
                return this.handleResponse('configure', payload);

            default:
                console.warn(`[WebcamSampler] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Response Handlers
    // =====================================

    _handleActivateResponse(payload) {
        if (payload?.success) {
            this.webcamActive = true;
            this._notifyStatusListeners();
        }
        this.handleResponse('activate', payload);
    }

    _handleDeactivateResponse(payload) {
        if (payload?.success) {
            this.webcamActive = false;
            this._notifyStatusListeners();
        }
        this.handleResponse('deactivate', payload);
    }

    _handleSampleResponse(payload) {
        if (payload?.success && payload.imageBase64) {
            const sample = {
                imageBase64: payload.imageBase64,
                timestamp: payload.timestamp || Date.now()
            };
            this.recentSamples.unshift(sample);
            if (this.recentSamples.length > 20) {
                this.recentSamples.pop();
            }
            this.sampleCount++;
            this.lastSampleTimestamp = sample.timestamp;
            this._notifySampleListeners(sample);
        }
        this.handleResponse('sample', payload);
    }

    _handleStatusResponse(payload) {
        if (payload?.success) {
            this.webcamActive = payload.webcamActive || false;
            this.samplingEnabled = payload.samplingEnabled || false;
            this.samplingInterval = payload.samplingInterval || this.samplingInterval;
            this.sampleCount = payload.sampleCount || 0;
            this.lastSampleTimestamp = payload.lastSampleTimestamp || null;
            this._notifyStatusListeners();
        }
        this.handleResponse('get_status', payload);
    }

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // API Methods
    // =====================================

    async activate() {
        return new Promise((resolve) => {
            this.sendMessage('activate', {});
            this._pendingRequests.set('activate', resolve);
        });
    }

    async deactivate() {
        return new Promise((resolve) => {
            this.sendMessage('deactivate', {});
            this._pendingRequests.set('deactivate', resolve);
        });
    }

    async captureSample() {
        return new Promise((resolve) => {
            this.sendMessage('sample', {});
            this._pendingRequests.set('sample', resolve);
        });
    }

    async startSampling(interval, variance) {
        const payload = {};
        if (interval !== undefined) payload.interval = interval;
        if (variance !== undefined) payload.variance = variance;

        return new Promise((resolve) => {
            this.sendMessage('start_sampling', payload);
            this._pendingRequests.set('start_sampling', (result) => {
                if (result?.success) {
                    this.samplingEnabled = true;
                    if (result.interval) this.samplingInterval = result.interval;
                    this._notifyStatusListeners();
                }
                resolve(result);
            });
        });
    }

    async stopSampling() {
        return new Promise((resolve) => {
            this.sendMessage('stop_sampling', {});
            this._pendingRequests.set('stop_sampling', (result) => {
                if (result?.success) {
                    this.samplingEnabled = false;
                    this._notifyStatusListeners();
                }
                resolve(result);
            });
        });
    }

    async getStatus() {
        return new Promise((resolve) => {
            this.sendMessage('get_status', {});
            this._pendingRequests.set('get_status', resolve);
        });
    }

    async configure(config) {
        return new Promise((resolve) => {
            this.sendMessage('configure', config);
            this._pendingRequests.set('configure', (result) => {
                if (result?.success) {
                    if (result.interval) this.samplingInterval = result.interval;
                    this._notifyStatusListeners();
                }
                resolve(result);
            });
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onStatusUpdate(callback) {
        this.statusCallbacks.add(callback);
        return () => this.statusCallbacks.delete(callback);
    }

    onSample(callback) {
        this.sampleCallbacks.add(callback);
        return () => this.sampleCallbacks.delete(callback);
    }

    _notifyStatusListeners() {
        const data = {
            webcamActive: this.webcamActive,
            samplingEnabled: this.samplingEnabled,
            samplingInterval: this.samplingInterval,
            sampleCount: this.sampleCount,
            lastSampleTimestamp: this.lastSampleTimestamp
        };
        for (const cb of this.statusCallbacks) {
            try { cb(data); } catch (err) { console.error('[WebcamSampler] Status callback error:', err); }
        }
    }

    _notifySampleListeners(sample) {
        for (const cb of this.sampleCallbacks) {
            try { cb(sample); } catch (err) { console.error('[WebcamSampler] Sample callback error:', err); }
        }
    }

    // =====================================
    // Getters
    // =====================================

    isWebcamActive() {
        return this.webcamActive;
    }

    isSamplingEnabled() {
        return this.samplingEnabled;
    }

    getRecentSamples() {
        return this.recentSamples;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[WebcamSampler] Cleaning up...');
        this.recentSamples = [];
        this.statusCallbacks.clear();
        this.sampleCallbacks.clear();
        this._pendingRequests.clear();
    }
}
